﻿import * as THREE from 'three/webgpu';
import { IS_DEBUG_ENABLED, loadGLTFModel, setToonMaterial } from './util';

const PATH_SEGMENTS = 50;
const BACKWARD_PATH_SEGMENTS = 50;
const BACKWARD_DISTANCE_BETWEEN_SEGMENTS = 0.5;
const MESH_SPACING_FACTOR = 0.44; // Adjust to control rail spacing (0-1, smaller = less gap)
const TRACK_INSTANCE_COUNT = 100;

export default class Tracks {
    scene: THREE.Scene;

    private readonly pathStart = new THREE.Vector2();
    private readonly pathDirection3D = new THREE.Vector3();
    private readonly pathForward = new THREE.Vector2();
    private readonly pathSide = new THREE.Vector2();
    private readonly pathControlPoint = new THREE.Vector2();
    private readonly pathEndPoint = new THREE.Vector2();

    private readonly pathPositionAttribute: THREE.BufferAttribute;
    private readonly backwardPathGeometry: THREE.BufferGeometry;
    private readonly backwardPathPositionAttribute: THREE.BufferAttribute;
    private backwardPathSeeded = false;
    private backwardPathCount = BACKWARD_PATH_SEGMENTS + 1;

    private readonly mainTrackGeometry: THREE.BufferGeometry;
    private readonly mainTrackPositionAttribute: THREE.BufferAttribute;
    private mainTrackCount = 0;

    private meshTemplate!: THREE.Object3D;
    private meshLength: number = 0;
    private meshInstances: THREE.Object3D[] = [];
    private cumulativeDistances: number[] = [];

    constructor(scene: THREE.Scene) {
        this.scene = scene;

        const pathPositions = new Float32Array((PATH_SEGMENTS + 1) * 3);
        const pathGeometry = new THREE.BufferGeometry().setAttribute(
            'position',
            new THREE.BufferAttribute(pathPositions, 3)
        );
        this.pathPositionAttribute = pathGeometry.getAttribute(
            'position'
        ) as THREE.BufferAttribute;

        const pathLine = new THREE.Line(
            pathGeometry,
            new THREE.LineBasicMaterial({ color: 0xff0000 })
        );
        pathLine.rotation.x = Math.PI / 2;

        pathLine.frustumCulled = false;
        this.scene.add(pathLine);

        /* --- */

        const backwardPathPositions = new Float32Array(
            (BACKWARD_PATH_SEGMENTS + 1) * 3
        );
        this.backwardPathGeometry = new THREE.BufferGeometry().setAttribute(
            'position',
            new THREE.BufferAttribute(backwardPathPositions, 3)
        );
        this.backwardPathPositionAttribute =
            this.backwardPathGeometry.getAttribute(
                'position'
            ) as THREE.BufferAttribute;
        const backwardPathLine = new THREE.Line(
            this.backwardPathGeometry,
            new THREE.LineBasicMaterial({ color: 0x0000ff })
        );
        backwardPathLine.rotation.x = Math.PI / 2;
        backwardPathLine.frustumCulled = false;
        this.scene.add(backwardPathLine);

        this.backwardPathGeometry.setDrawRange(0, this.backwardPathCount);
        this.backwardPathPositionAttribute.needsUpdate = true;

        /* --- */

        const mainTrackPositions = new Float32Array(
            (BACKWARD_PATH_SEGMENTS + 1 + PATH_SEGMENTS + 1) * 3
        );
        this.mainTrackGeometry = new THREE.BufferGeometry().setAttribute(
            'position',
            new THREE.BufferAttribute(mainTrackPositions, 3)
        );
        this.mainTrackPositionAttribute = this.mainTrackGeometry.getAttribute(
            'position'
        ) as THREE.BufferAttribute;
        // const mainTrackLine = new THREE.Line(
        //     this.mainTrackGeometry,
        //     new THREE.LineBasicMaterial({ color: 0x00000000 })
        // );
        // mainTrackLine.rotation.x = Math.PI / 2;
        // mainTrackLine.frustumCulled = false;
        // this.scene.add(mainTrackLine);

        this.mainTrackGeometry.setDrawRange(0, this.mainTrackCount);
        this.mainTrackPositionAttribute.needsUpdate = true;
    }

    private seedBackwardPath(lastPosition: THREE.Vector3) {
        const stepX = -this.pathForward.x * BACKWARD_DISTANCE_BETWEEN_SEGMENTS;
        const stepY = -this.pathForward.y * BACKWARD_DISTANCE_BETWEEN_SEGMENTS;

        for (let index = 0; index <= BACKWARD_PATH_SEGMENTS; index += 1) {
            this.backwardPathPositionAttribute.setXYZ(
                index,
                lastPosition.x + stepX * (BACKWARD_PATH_SEGMENTS - index),
                lastPosition.z + stepY * (BACKWARD_PATH_SEGMENTS - index),
                0
            );
        }

        this.backwardPathCount = BACKWARD_PATH_SEGMENTS + 1;
        this.backwardPathGeometry.setDrawRange(0, this.backwardPathCount);
        this.backwardPathPositionAttribute.needsUpdate = true;
        this.backwardPathSeeded = true;
    }

    public async create() {
        if (!IS_DEBUG_ENABLED) {
            // Load template mesh for rail segments
            this.meshTemplate = await loadGLTFModel('./tracks.glb');
            this.meshTemplate.children.forEach((group: THREE.Object3D) => {
                group.traverse((child) => {
                    const mesh = child as THREE.Mesh;
                    if (mesh.isMesh) {
                        const materials = Array.isArray(mesh.material)
                            ? mesh.material
                            : [mesh.material];

                        materials.forEach((material) => {
                            if (material.name === 'tracks') {
                                setToonMaterial(material, mesh);
                            }
                        });
                    }
                });
            });

            // Calculate mesh length from bounding box
            const bbox = new THREE.Box3().setFromObject(this.meshTemplate);
            this.meshLength = bbox.getSize(new THREE.Vector3()).length();

            while (this.meshInstances.length < TRACK_INSTANCE_COUNT) {
                const instance = this.meshTemplate.clone();
                this.scene.add(instance);
                this.meshInstances.push(instance);
            }
        }
    }

    // Remove points from the backward path when the locomotive returns close to them
    private returnerDetection(newX: number, newY: number, minDistSq: number) {
        for (let index = this.backwardPathCount - 1; index >= 0; index -= 1) {
            const px = this.backwardPathPositionAttribute.getX(index);
            const py = this.backwardPathPositionAttribute.getY(index);
            const dx = newX - px;
            const dy = newY - py;

            if (dx * dx + dy * dy < minDistSq) {
                if (index < this.backwardPathCount - 1) {
                    this.backwardPathCount = index + 1;
                    this.backwardPathGeometry.setDrawRange(
                        0,
                        this.backwardPathCount
                    );
                    this.backwardPathPositionAttribute.needsUpdate = true;
                }

                return;
            }
        }
    }

    private drawBackwardsPath(lastPosition: THREE.Vector3) {
        const newX = lastPosition.x;
        const newY = lastPosition.z;

        if (!this.backwardPathSeeded) {
            this.seedBackwardPath(lastPosition);
            return;
        }

        const minDist = BACKWARD_DISTANCE_BETWEEN_SEGMENTS;
        const minDistSq = minDist * minDist;

        this.returnerDetection(newX, newY, minDistSq);

        const lastIndex = this.backwardPathCount - 1;
        const tailX = this.backwardPathPositionAttribute.getX(lastIndex);
        const tailY = this.backwardPathPositionAttribute.getY(lastIndex);
        const tailDx = newX - tailX;
        const tailDy = newY - tailY;
        // If the locomotive hasn't moved far enough from the last point in the backward path, don't add a new point
        if (tailDx * tailDx + tailDy * tailDy < minDistSq) {
            return;
        }

        if (this.backwardPathCount < BACKWARD_PATH_SEGMENTS + 1) {
            this.backwardPathPositionAttribute.setXYZ(
                this.backwardPathCount,
                newX,
                newY,
                0
            );
            this.backwardPathCount += 1;
        } else {
            for (let index = 0; index < BACKWARD_PATH_SEGMENTS; index += 1) {
                this.backwardPathPositionAttribute.setXYZ(
                    index,
                    this.backwardPathPositionAttribute.getX(index + 1),
                    this.backwardPathPositionAttribute.getY(index + 1),
                    0
                );
            }

            this.backwardPathPositionAttribute.setXYZ(
                BACKWARD_PATH_SEGMENTS,
                newX,
                newY,
                0
            );
        }

        this.backwardPathGeometry.setDrawRange(0, this.backwardPathCount);
        this.backwardPathPositionAttribute.needsUpdate = true;
    }

    private drawPath(
        locomotive: THREE.Object3D,
        rotate: number,
        frontOffset: number = 0
    ) {
        const distance = 15;

        locomotive.getWorldDirection(this.pathDirection3D);
        this.pathForward
            .set(this.pathDirection3D.x, this.pathDirection3D.z)
            .normalize();

        const startPoint = this.pathStart.set(
            locomotive.position.x,
            locomotive.position.z
        );
        startPoint.addScaledVector(this.pathForward, -frontOffset);

        const controlPoint = this.pathControlPoint.copy(startPoint);
        controlPoint.addScaledVector(this.pathForward, distance);

        const endPoint = this.pathEndPoint.copy(startPoint);
        endPoint.addScaledVector(this.pathForward, distance * 2);

        this.pathSide.set(this.pathForward.y, -this.pathForward.x).normalize();
        endPoint.addScaledVector(this.pathSide, -rotate * distance);

        for (let index = 0; index <= PATH_SEGMENTS; index += 1) {
            const t = index / PATH_SEGMENTS;
            const invT = 1 - t;
            const startWeight = invT * invT;
            const controlWeight = 2 * invT * t;
            const endWeight = t * t;

            // prettier-ignore
            this.pathPositionAttribute.setXYZ(
                index,
                startPoint.x * startWeight + controlPoint.x * controlWeight + endPoint.x * endWeight,
                startPoint.y * startWeight + controlPoint.y * controlWeight + endPoint.y * endWeight,
                0
            );
        }

        this.pathPositionAttribute.needsUpdate = true;
    }

    private updateMeshInstances(position: THREE.Vector3): void {
        if (this.meshInstances.length === 0) {
            return;
        }

        const spacing = Math.max(0.001, this.meshLength * MESH_SPACING_FACTOR);
        const centerOffset = (TRACK_INSTANCE_COUNT - 1) * 0.5;

        // Keep the train centered and recycle segments by snapping the strip to spacing.
        const snappedOriginZ =
            position.z - THREE.MathUtils.euclideanModulo(position.z, spacing);

        for (let i = 0; i < TRACK_INSTANCE_COUNT; i += 1) {
            const instance = this.meshInstances[i];
            instance.position.x = 0;
            instance.position.y = 0;
            instance.position.z = snappedOriginZ + (i - centerOffset) * spacing;
        }
    }

    private getPointOnMainTrack(index: number): {
        x: number;
        y: number;
        index: number;
    } {
        if (index < 0 || index >= this.mainTrackCount) {
            return { x: 0, y: 0, index: 0 };
        }
        return {
            x: this.mainTrackPositionAttribute.getX(index),
            y: this.mainTrackPositionAttribute.getY(index),
            index,
        };
    }

    private drawMainTrack() {
        let pointIndex = 0;

        for (let i = 0; i < this.backwardPathCount; i += 1) {
            this.mainTrackPositionAttribute.setXYZ(
                pointIndex,
                this.backwardPathPositionAttribute.getX(i),
                this.backwardPathPositionAttribute.getY(i),
                0
            );
            pointIndex += 1;
        }

        for (let i = 0; i <= PATH_SEGMENTS; i += 1) {
            this.mainTrackPositionAttribute.setXYZ(
                pointIndex,
                this.pathPositionAttribute.getX(i),
                this.pathPositionAttribute.getY(i),
                0
            );
            pointIndex += 1;
        }

        this.mainTrackCount = pointIndex;
        this.mainTrackGeometry.setDrawRange(0, this.mainTrackCount);
        this.mainTrackPositionAttribute.needsUpdate = true;

        // Recompute cumulative distances along the track
        this.computeCumulativeDistances();
    }

    private getTangentAndCurvature(index: number): {
        tangent: THREE.Vector2;
        curvature: number;
    } {
        const tangent = new THREE.Vector2();
        let curvature = 0;

        if (index < 0 || index >= this.mainTrackCount) {
            return { tangent, curvature };
        }

        // Get previous, current, and next points
        const prevIdx = Math.max(0, index - 1);
        const nextIdx = Math.min(this.mainTrackCount - 1, index + 1);

        const prev = this.getPointOnMainTrack(prevIdx);
        const curr = this.getPointOnMainTrack(index);
        const next = this.getPointOnMainTrack(nextIdx);

        // Calculate tangent direction
        const dx = next.x - prev.x;
        const dy = next.y - prev.y;
        tangent.set(dx, dy).normalize();

        // Calculate curvature using cross product
        const edge1 = new THREE.Vector2(curr.x - prev.x, curr.y - prev.y);
        const edge2 = new THREE.Vector2(next.x - curr.x, next.y - curr.y);

        const len1 = edge1.length();
        const len2 = edge2.length();

        if (len1 > 0 && len2 > 0) {
            edge1.normalize();
            edge2.normalize();
            // Cross product gives signed curvature
            curvature = edge1.x * edge2.y - edge1.y * edge2.x;
        }

        return { tangent, curvature };
    }

    private getTangentAndCurvatureAtDistance(distance: number): {
        tangent: THREE.Vector2;
        curvature: number;
    } {
        const tangent = new THREE.Vector2();
        let curvature = 0;

        if (this.cumulativeDistances.length < 2) {
            return { tangent, curvature };
        }

        // Find the index bracket for this distance
        let lowIdx = 0;
        for (let i = 0; i < this.mainTrackCount; i++) {
            if (this.cumulativeDistances[i] <= distance) {
                lowIdx = i;
            } else {
                break;
            }
        }

        const highIdx = Math.min(lowIdx + 1, this.mainTrackCount - 1);

        if (lowIdx === highIdx) {
            return this.getTangentAndCurvature(lowIdx);
        }

        // Get tangents at both points and interpolate
        const lowTangent = this.getTangentAndCurvature(lowIdx);
        const highTangent = this.getTangentAndCurvature(highIdx);

        const lowDist = this.cumulativeDistances[lowIdx];
        const highDist = this.cumulativeDistances[highIdx];
        const segmentDist = highDist - lowDist;

        let t = 0;
        if (segmentDist > 0.0001) {
            t = (distance - lowDist) / segmentDist;
        }
        t = Math.max(0, Math.min(1, t));

        // Interpolate tangent
        tangent
            .copy(lowTangent.tangent)
            .multiplyScalar(1 - t)
            .addScaledVector(highTangent.tangent, t)
            .normalize();

        // Interpolate curvature
        curvature = lowTangent.curvature * (1 - t) + highTangent.curvature * t;

        return { tangent, curvature };
    }

    private computeCumulativeDistances(): void {
        this.cumulativeDistances = [];
        this.cumulativeDistances[0] = 0;

        for (let i = 1; i < this.mainTrackCount; i++) {
            const prevPoint = this.getPointOnMainTrack(i - 1);
            const currPoint = this.getPointOnMainTrack(i);

            const dx = currPoint.x - prevPoint.x;
            const dy = currPoint.y - prevPoint.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            this.cumulativeDistances[i] =
                this.cumulativeDistances[i - 1] + distance;
        }
    }

    private getPointAtDistance(targetDistance: number): {
        x: number;
        y: number;
        index: number;
    } {
        if (this.cumulativeDistances.length === 0) {
            return { x: 0, y: 0, index: 0 };
        }

        // Clamp target distance to valid range
        targetDistance = Math.max(0, targetDistance);

        // Find the two points that bracket this distance
        let lowIdx = 0;
        let highIdx = this.mainTrackCount - 1;

        for (let i = 0; i < this.mainTrackCount; i++) {
            if (this.cumulativeDistances[i] <= targetDistance) {
                lowIdx = i;
            } else {
                break;
            }
        }

        highIdx = Math.min(lowIdx + 1, this.mainTrackCount - 1);

        if (lowIdx === highIdx) {
            const pt = this.getPointOnMainTrack(lowIdx);
            return { x: pt.x, y: pt.y, index: lowIdx };
        }

        // Interpolate between lowIdx and highIdx
        const lowDist = this.cumulativeDistances[lowIdx];
        const highDist = this.cumulativeDistances[highIdx];
        const segmentDist = highDist - lowDist;

        let t = 0;
        if (segmentDist > 0.0001) {
            t = (targetDistance - lowDist) / segmentDist;
        }
        t = Math.max(0, Math.min(1, t));

        const lowPt = this.getPointOnMainTrack(lowIdx);
        const highPt = this.getPointOnMainTrack(highIdx);

        return {
            x: lowPt.x + (highPt.x - lowPt.x) * t,
            y: lowPt.y + (highPt.y - lowPt.y) * t,
            index: lowIdx,
        };
    }

    private positionMeshOnTrackAtDistance(
        meshInstance: THREE.Object3D,
        distance: number
    ): void {
        const point = this.getPointAtDistance(distance);
        const { tangent, curvature } =
            this.getTangentAndCurvatureAtDistance(distance);

        // Position mesh
        meshInstance.position.x = point.x;
        meshInstance.position.z = point.y;
        meshInstance.position.y = 0;

        // Rotate mesh to follow track tangent
        const angle = Math.atan2(tangent.x, tangent.y);
        meshInstance.rotation.y = angle;

        // Apply slight tilt based on curvature for more visual deformation
        meshInstance.rotation.z = curvature * 0.1;
    }

    public snapToBackwardTrack(mesh: THREE.Object3D, distance: number): void {
        if (
            this.backwardPathCount < 2 ||
            this.cumulativeDistances.length === 0
        ) {
            return;
        }

        // The backward path is the first part of the main track
        const backwardPathEnd = this.backwardPathCount - 1;
        if (backwardPathEnd >= this.cumulativeDistances.length) {
            return;
        }

        // Max distance available in backward path
        const maxBackwardDistance = this.cumulativeDistances[backwardPathEnd];

        if (maxBackwardDistance <= 0.0001) {
            return;
        }

        // Reverse distance starts at the current locomotive position,
        // which is the end of the backward path, then moves toward older points.
        const wrappedDistance = distance % maxBackwardDistance;
        const targetDistance = maxBackwardDistance - wrappedDistance;

        // Use the wrapped distance to index into backward path
        const point = this.getPointAtDistance(targetDistance);

        mesh.position.x = point.x;
        mesh.position.z = point.y;
        mesh.position.y = 0;

        // Get tangent for rotation
        const { tangent } =
            this.getTangentAndCurvatureAtDistance(targetDistance);
        const angle = Math.atan2(tangent.x, tangent.y);
        mesh.rotation.y = angle;
    }

    public placeMeshAtDistance(
        meshInstance: THREE.Object3D,
        distance: number
    ): void {
        this.positionMeshOnTrackAtDistance(meshInstance, distance);
    }

    public animate(locomotive: THREE.Object3D, rotate: number) {
        if (IS_DEBUG_ENABLED) {
            this.drawPath(locomotive, rotate);
            this.drawBackwardsPath(locomotive.position as THREE.Vector3);
            this.drawMainTrack();
        }
        this.updateMeshInstances(locomotive.position);
    }
}
